import { proxy, ref } from 'valtio'
import { DateTime } from 'luxon'
import { IPoint } from '../interfaces/types.ts'
import { isPointLocationValid } from '../helpers/locationStatusHelper.ts'
import { controlState } from './controlState.ts'

export interface IHistoryStep {
    location: IPoint
    age: number
}

const isoSort = (a: IPoint, b: IPoint) => {
    return (a?.properties?.receivedTime < b?.properties?.receivedTime) ? -1
        : ((a?.properties?.receivedTime > b?.properties?.receivedTime) ? 1 : 0)
}

export const historyState = proxy({
    historyPets: [] as string[],
    selectedPetId: '',
    startDate: ref(DateTime.now()) as unknown as DateTime,
    endDate: ref(DateTime.now()) as unknown as DateTime,
    earliestDate: ref(DateTime.now()) as unknown as DateTime,
    prevDate: ref({ start: DateTime.now(), end: DateTime.now() }) as unknown as { start: DateTime, end: DateTime },
    locations: [] as IPoint[],
    validLocations: [] as IPoint[],
    bounds: null as google.maps.LatLngBounds | null,
    markers: [] as any[],
    playIndex: 0,
    intervalTimer: 0 as any,
    playbackSpeed: 'fast' as 'fast' | 'slow',
    historyPerPet: {} as Record<string, IPoint[]>,
    highlight: null as IPoint | null,
    currentIndex: 0,
    displayTen: true,
    isMovieMode: false,
    playDirection: 'forward' as 'forward' | 'reverse',
    map: null as google.maps.Map | null,
    includeInvalid: true,
    showSafeZones: false,
    reachedStart: false,
    noMoreOlder: false,
    petsInView: [] as string[],
    markerWindowStart: 0,
})

// --- Action functions ---

/** Sets locations and computes derived validLocations + bounds */
export function setHistoryLocations(l: IPoint[]) {
    const valid = l?.filter(p => isPointLocationValid(p)).sort(isoSort) || []
    const bounds = new google.maps.LatLngBounds()
    valid.forEach(v => bounds.extend({
        lat: v?.properties?.latitude,
        lng: v.properties?.longitude
    }))
    historyState.locations = l
    historyState.validLocations = valid
    historyState.bounds = ref(bounds) as any
}

/** Bulk update multiple fields at once — wraps DateTime values with ref() */
export function setHistoryStateUpdate(update: any) {
    if (!update) return
    // Wrap DateTime values with ref() to prevent Valtio from deep-proxying them
    const wrapped = { ...update }
    for (const key of Object.keys(wrapped)) {
        if (wrapped[key] instanceof DateTime) {
            wrapped[key] = ref(wrapped[key])
        }
    }
    Object.assign(historyState, wrapped)
}

export function setHistoryMap(map: google.maps.Map | null) {
    historyState.map = map ? ref(map) as any : null
}

export function resetHistory() {
    historyState.historyPets = []
    historyState.startDate = ref(DateTime.now()) as unknown as DateTime
    historyState.endDate = ref(DateTime.now()) as unknown as DateTime
    historyState.locations = []
    historyState.historyPerPet = {}
    historyState.validLocations = []
    historyState.markers = []
    historyState.playIndex = 0
    historyState.intervalTimer = 0
    historyState.bounds = null
    historyState.playbackSpeed = 'fast'
    historyState.highlight = null
    historyState.map = null
    historyState.includeInvalid = !controlState.isOnMobileDevice
    historyState.noMoreOlder = false
    historyState.petsInView = []
    historyState.markerWindowStart = 0
    historyState.currentIndex = 0
    historyState.displayTen = true
    historyState.isMovieMode = false
    historyState.playDirection = 'forward'
}

export default historyState
